import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { companiesApi } from "@/api/companies";
import { queryKeys } from "@/lib/query-keys";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Building2 } from "lucide-react";
import { TenantModelSettings } from "./TenantModelSettings";

/**
 * MSP admin view of a single tenant's LLM provider config.
 *
 * Replaces the previous inline TenantModelSettings modal that opened on
 * top of the /tenants list. Lives at /tenants/:tenantId/admin/models
 * inside the AdminShell so the MSP admin stays in platform-admin
 * context while configuring the tenant.
 *
 * TenantModelSettings itself still takes an onClose prop (it was built
 * as a modal) — we wire that to navigate back to /tenants so the close
 * button keeps working.
 */
export function TenantAdminModels() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();

  const { data: company, isLoading } = useQuery({
    queryKey: queryKeys.companies.detail(tenantId!),
    queryFn: () => companiesApi.get(tenantId!),
    enabled: !!tenantId,
  });

  if (!tenantId) return null;

  if (isLoading) {
    return (
      <div className="p-6">
        <p className="text-sm text-text-muted">Loading tenant…</p>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="p-6">
        <p className="text-sm text-text-muted">Tenant not found.</p>
        <Button
          size="sm"
          variant="ghost"
          className="mt-3"
          onClick={() => navigate("/tenants")}
        >
          <ArrowLeft size={14} className="mr-1.5" />
          Back to tenants
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      {/* Header bar — keeps the MSP admin oriented */}
      <div className="shrink-0 border-b border-border bg-background px-6 py-3">
        <div className="flex items-center gap-3">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => navigate("/tenants")}
          >
            <ArrowLeft size={14} className="mr-1.5" />
            Tenants
          </Button>
          <span className="text-text-muted">/</span>
          <span className="flex items-center gap-1.5 text-sm font-medium text-text-primary">
            <Building2 size={14} />
            {company.name}
          </span>
          <span className="text-text-muted">/</span>
          <span className="text-sm text-text-secondary">Model settings</span>
          <Button
            size="sm"
            variant="outline"
            className="ml-auto"
            onClick={() => navigate(`/tenants/${tenantId}/admin/usage`)}
          >
            Token usage
          </Button>
        </div>
      </div>

      {/* Reuse the existing modal body; its onClose just navigates back */}
      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
        <TenantModelSettings
          tenantId={tenantId}
          tenantName={company.name}
          onClose={() => navigate("/tenants")}
        />
      </div>
    </div>
  );
}
